/**
 * STEP NINE — "Specimen gets CRANKY without its precious oxygen."
 *
 * Step 8 wired up gas exchange (lungs + diaphragm + atmosphere). Step 9
 * adds the BRAIN — and quietly introduces the "body absorbs nutrients"
 * concept off-screen: the left wall takes O₂ in at the top and emits
 * enriched blood (food + O₂ + CO₂) at the bottom. The narrative is that
 * the body's intestines (not yet in the pool) silently add food and CO₂
 * to the bloodstream between those two wall ports — the student just sees
 * the input/output of that hidden pipeline.
 *
 * Wall ports:
 *   TOP   atmosphere bidirectional       (trachea endpoint)
 *   LEFT-upper  receptacle [oxygen]      (body absorbs O₂)
 *   LEFT-lower  spout [food, oxygen, co2] hideContents (discovery — the
 *               student wires it up and discovers the body has been
 *               quietly enriching the blood with nutrients)
 *
 * Intended solution — every cone in the loop:
 *   heart.A-out (always-on CO₂) → lungs.left-in
 *   atmosphere ↔ lungs.top + diaphragm castellated under lungs
 *   lungs.right-out (O₂ after gas exchange) → LEFT-upper (body absorbs O₂)
 *   LEFT-lower (enriched blood) → brain.A-in (brain consumes food+O₂+CO₂)
 *   brain.A-out (CO₂) → heart.A-in (CO₂ returns to heart, closing the loop)
 *
 * Heart's B-side (B-in/B-out) stays unused at Step 9 — they're inert
 * receptacles/conditional spouts so they don't burst or spurt.
 */
import {
  leftReceptacle,
  leftSpout,
  topAtmosphere,
  type LevelConfig,
} from './portHelpers';
import type { MoleculeMix } from '../types';

export const LEVEL_9: LevelConfig = {
  step: 9,
  title: 'STEP NINE',
  instructions: 'Specimen gets CRANKY without its precious oxygen.',
  // Trachea (atmosphere ↔ lungs top) is mandatory — without it the
  // bidirectional atmosphere could be piped straight into the O₂ receptacle,
  // bypassing the lungs. See LevelConfig.requiredLinks.
  requiredLinks: [{ a: 's9-atmos', b: 's9-lungs-top' }],
  ports: [
    topAtmosphere('s9-atmos', 8, ['oxygen']),
    // Body absorbs O₂ here (top of left wall — hint visible).
    leftReceptacle('s9-o2-in', 3, ['oxygen']),
    // Body releases enriched blood here. Discovery — the student wires
    // this to the brain to see [food, oxygen, co2] flow out together.
    // (Narratively, the intestines are between the two wall ports, adding
    // food + CO₂ to the bloodstream — but the intestines aren't in this
    // level's pool yet.) Fires only after O₂ has arrived at the upper-left
    // receptacle, mirroring Level 6's wall-port `dependsOn` pattern.
    {
      ...leftSpout('s9-blood-out', 13, ['food', 'oxygen', 'co2']),
      hideContents: true,
      dependsOn: ['s9-o2-in'],
    },
  ],
  organPool: [
    // ---------- Heart ----------
    {
      id: 'heart',
      art: 'heart',
      cols: 4,
      rows: 4,
      activatesWhen: ['s9-heart-A-out', 's9-heart-B-out'],
      ports: [
        {
          id: 's9-heart-A-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 0, dRow: -1 },
          carries: ['co2'],
        },
        {
          id: 's9-heart-A-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 0, dRow: 3 },
          carries: ['co2'],
        },
        {
          id: 's9-heart-B-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 3, dRow: -1 },
          carries: ['oxygen'],
        },
        {
          id: 's9-heart-B-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 3, dRow: 3 },
          carries: ['oxygen'],
          dependsOn: ['s9-heart-B-in'],
        },
      ],
    },
    // ---------- Lungs ----------
    {
      id: 'lungs',
      art: 'lungs',
      cols: 5,
      rows: 3,
      activatesWhen: ['s9-lungs-right-out'],
      ports: [
        {
          id: 's9-lungs-top',
          kind: 'bidirectional',
          facing: 'N',
          cellOffset: { dCol: 2, dRow: -1 },
          carries: ['oxygen'],
          coneFlipped: true,
          transform: (inputs) => {
            const left = inputs.get('s9-lungs-left-in') ?? [];
            const bottom = inputs.get('s9-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            const tracheaInputArriving = (inputs.get('s9-lungs-top') ?? []).length > 0;
            if (operating && tracheaInputArriving && left.includes('co2')) {
              return ['co2'];
            }
            return null;
          },
        },
        {
          id: 's9-lungs-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['co2'],
          acceptsAny: true,
          validIf: (inputs) => {
            const bottom = inputs.get('s9-lungs-bottom-pulse') ?? [];
            return bottom.includes('pulse');
          },
        },
        {
          id: 's9-lungs-right-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 5, dRow: 0 },
          carries: ['oxygen'],
          transform: (inputs) => {
            const left = inputs.get('s9-lungs-left-in') ?? [];
            const bottom = inputs.get('s9-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            if (!operating) return null;
            if (left.length === 0) return null;
            const tracheaConnected = (inputs.get('s9-lungs-top') ?? []).length > 0;
            if (!tracheaConnected) {
              return left as MoleculeMix;
            }
            const out = new Set(left.filter((m) => m !== 'co2'));
            out.add('oxygen');
            return Array.from(out);
          },
        },
        {
          id: 's9-lungs-bottom-pulse',
          kind: 'receptacle',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    // ---------- Diaphragm ----------
    {
      id: 'diaphragm',
      art: 'diaphragm',
      cols: 3,
      rows: 2,
      ports: [
        {
          id: 's9-diaphragm-top',
          kind: 'spout',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    // ---------- Brain ----------
    // Step-9-specific: accepts the body's enriched blood [food, oxygen, co2]
    // straight from the wall — no intestines in this level's pool, so the
    // brain port matches the wall spout exactly.
    {
      id: 'brain',
      art: 'brain',
      cols: 3,
      rows: 3,
      activatesWhen: ['s9-brain-out'],
      ports: [
        {
          id: 's9-brain-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen', 'co2'],
        },
        {
          id: 's9-brain-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 3, dRow: 1 },
          carries: ['co2'],
          dependsOn: ['s9-brain-in'],
        },
      ],
    },
  ],
};
